import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'underscore';
import { useFocus } from 'js/hooks/useFocus';
import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { clsb, clsx } from 'js/utils/classNames.util';

import * as Types from './types';
import { Label } from '../Label';
import { CheckboxBare } from '../Checkbox';
import * as helpers from '../../helpers';
import { withFinalFormField } from '../../hocs';
import { FieldBottomMessage } from '../FieldBottomMessage';
import { calculateDropdownPositionTop } from './helpers';

import '../../FormFields.scss';
import style from './MultiSelect.scss';

export const MultiSelectBare: React.FC<Types.IMultiSelectProps> = ({
    meta,
    hint,
    label,
    onChange,
    onFocus,
    onBlur,
    readOnly,
    disabled,
    leftIcon,
    rightIcon,
    className,
    classNames,
    infoTooltip,
    onKeyDown,
    onMouseDown,
    options = [],
    currencySymbol,
    placeholder = '',
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownTopPosition, setDropdownTopPosition] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const innerContainerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownFocusAnchorRef, focusDropdownAnchor] = useFocus<HTMLSpanElement>();
    const translate = useTranslations('playtech.mobile.system.multiselect.');
    const isDisabled = meta?.submitting || meta?.validating || disabled;

    const values = useMemo(
        () => `${props.value || props.defaultValue || ''}`?.split(',') || [],
        [props.value, props.defaultValue]
    );

    const selectedOptions = useMemo(() => options.filter(item => values.includes(item.value)), [values, options]);

    const onCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;

        const newSelectedOptions = checked
            ? options.filter(item => values.includes(item.value) || item.value === name)
            : options.filter(item => values.includes(item.value) && item.value !== name);

        const newValue = newSelectedOptions
            .map(item => item.value)
            .join(',');

        onChange && onChange({ target: { value: newValue } });
    }, [values, options, onChange]);

    const onCombinedKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        onKeyDown?.(event);

        if (readOnly || isDisabled) {
            return;
        }

        if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter') {
            event.preventDefault();

            setIsOpen((prevState) => !prevState);
        }
    }, [onKeyDown, readOnly, isDisabled]);

    const onCombinedMouseDown = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
        onMouseDown?.(event);

        setIsOpen((prevState) => !prevState);
    }, [onMouseDown]);

    useEffect(() => {
        const closeOnMouseDown = (event: MouseEvent) => {
            if (dropdownRef.current?.contains(event.target as Node)
                || innerContainerRef.current?.contains(event.target as Node)) {
                return;
            }

            setIsOpen(false);
        };

        const handleKeyUp = (event: KeyboardEvent) => {
            const { key } = event;

            if (!isOpen) {
                return;
            }

            if (key === 'Escape') {
                setIsOpen(false);
                innerContainerRef.current?.focus();

                return;
            }

            if (key === 'Tab' && !dropdownRef.current?.contains(document.activeElement as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            window.addEventListener('mousedown', closeOnMouseDown);
            document.body.addEventListener('keyup', handleKeyUp);
        }

        return () => {
            window.removeEventListener('mousedown', closeOnMouseDown);
            document.body.removeEventListener('keyup', handleKeyUp);
        };
    }, [isOpen]);

    useLayoutEffect(() => {
        const top = calculateDropdownPositionTop(innerContainerRef.current, dropdownRef.current);

        if (top !== dropdownTopPosition) {
            setDropdownTopPosition(top);
        }

        if (isOpen) {
            dropdownRef.current && (dropdownRef.current.scrollTop = 0);

            focusDropdownAnchor();
        }
    }, [isOpen, focusDropdownAnchor, innerContainerRef, dropdownRef, dropdownTopPosition]);

    const handleScroll = useCallback(() => {
        const top = calculateDropdownPositionTop(innerContainerRef.current, dropdownRef.current);

        if (top !== dropdownTopPosition && !isOpen) {
            setDropdownTopPosition(top);
        }
    }, [isOpen, setDropdownTopPosition, dropdownTopPosition]);

    useEffect(() => {
        const debouncedHandleScroll = debounce(handleScroll, 100);

        window.addEventListener('scroll', debouncedHandleScroll);

        return () => {
            window.removeEventListener('scroll', debouncedHandleScroll);
        };
    }, [handleScroll]);

    const displayValue = selectedOptions.length
        ? selectedOptions.map(item => item.label).join(', ')
        : `${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`;

    const counter = selectedOptions.length;

    const interactionClass = helpers.getInteractionClass({
        readOnly,
        error: meta?.error,
        disabled: isDisabled,
        active: isOpen || meta?.active,
        touched: meta?.touched,
        submitting: meta?.submitting
    });
    const containerClassName = clsx([
        clsb([style, classNames], ['container', ...selectedOptions.length > 0 ? [] : ['empty']]),
        interactionClass,
        className
    ]);

    return (
        <div className={containerClassName} ref={containerRef}>
            {(label || isLabelAsteriskEnabled) && (
                <Label
                    label={label}
                    tabIndex={-1}
                    readOnly={readOnly}
                    classNames={classNames}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                />
            )}

            <div
                onBlur={onBlur}
                onFocus={onFocus}
                className={clsb([style, classNames], ['controlWrapper'])}
            >
                <div
                    role="button"
                    ref={innerContainerRef}
                    className={clsb([style, classNames], ['innerContainer'])}
                    {...props}
                    {
                        ...!isDisabled && !readOnly && {
                            tabIndex: 0,
                            onKeyDown: onCombinedKeyDown,
                            onMouseDown: onCombinedMouseDown
                        }
                    }
                >
                    {leftIcon && leftIcon()}

                    {currencySymbol && <span className={clsb([style, classNames], ['currencySymbol'])}>{currencySymbol}</span>}

                    <span className={clsb([style, classNames], ['displayValue'])}>{displayValue}</span>

                    {counter > 0 && (
                        <span
                            aria-label={translate('counter.aria-label', [counter])}
                            className={clsb([style, classNames], ['counter'])}>{counter}</span>
                    )}
                    {rightIcon && rightIcon()}

                    <Icon
                        type="arrowDropdown"
                        disabled={isDisabled || readOnly}
                        className={clsb([style, classNames], ['dropDownIcon'])}
                    />
                </div>
                <div
                    ref={dropdownRef}
                    className={clsb([style, classNames], ['dropDownContainer'])}
                    style={{
                        top: `${dropdownTopPosition}px`,
                        ...!isOpen && { visibility: 'hidden' }
                    }}
                >
                    <span tabIndex={-1} ref={dropdownFocusAnchorRef}></span>
                    <ul>
                        {options.map(item => (
                            <li key={item.key || item.value}>
                                <CheckboxBare
                                    name={item.value}
                                    label={item.label}
                                    aria-label={item.label}
                                    onChange={onCheckboxChange}
                                    checked={values.includes(item.value)}
                                    classNames={{
                                        container: clsb([style, classNames], ['dropDownCheckboxContainer']),
                                        containerInner: clsb([style, classNames], ['dropDownItemLabel']),
                                        checkbox: clsb([style, classNames], ['dropDownCheckbox'])
                                    }}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <FieldBottomMessage hint={hint} meta={meta} classNames={classNames} />
        </div>
    );
};

export const MultiSelectPure = memo(MultiSelectBare);
export const MultiSelect = withFinalFormField<string>()(MultiSelectPure);
